import './App.css';
import React, { useState, useEffect } from 'react';
import Header from './components/Header.js';
import CardSection from './components/CardSection.js';
import SearchSection from './components/SearchSection.js';
import FeedbackSection from './components/FeedbackSection.js';
import Footer from './components/Footer.js';
import dayjs from 'dayjs';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

function App() {
  const [actorPair, setActorPair] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedActor, setSelectedActor] = useState(null);
  const [currentActor, setCurrentActor] = useState(null);
  const [incorrectSelection, setIncorrectSelection] = useState(false);
  const [connectedActors, setConnectedActors] = useState([])
  const [completeConnection, setCompleteConnection] = useState(false);
  const [connectedMovies, setConnectedMovies] = useState([]);
  const [date, setDate] = useState(dayjs());

  useEffect(() => {
    const fetchActors = async () => {
      const formattedDate = date.format('YYYY-MM-DD');
      console.log('Fetching actors for date:', formattedDate);
      try {
        console.log('Fetching actors...');
        const response = await fetch(`https://web-production-6109.up.railway.app/api/daily-challenge/?date=${formattedDate}`);
        
        if (!response.ok) {
          throw new Error("Failed to fetch actors");
        }
        
        const data = await response.json();
        setSearchQuery('');
        setSearchResults([]);
        setSelectedActor(null);
        setIncorrectSelection(false);
        setConnectedActors([]);
        setCompleteConnection(false);
        setConnectedMovies([]);
        setActorPair({actor_1:data.actor_1, actor_2:data.actor_2});
        setCurrentActor(data.actor_1);
        console.log(data);
      } catch (error) {
        setError(error.message);
      }
    };
  
    fetchActors();
  }, [date]);

  // Log whenever date changes
  useEffect(() => {
    console.log('Date state updated:', date.format('YYYY-MM-DD'));
  }, [date]);

  useEffect(() => {
    if (searchQuery.length >2) {
      fetch(`https://web-production-6109.up.railway.app/api/search-actors/?query=${searchQuery}`)
        .then((response) => response.json())
        .then((data) => setSearchResults(data))
        .catch((error) => setError(error.message))
    }
    else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSelectActor = (actorName) => {
    setSearchQuery('');
    setSearchResults([]);
    validateActorSelection(actorName);
  }

  const fetchActorData = async (actorName, retries = 3, delay = 1000) => {
    try {
      const response = await fetch(`https://web-production-6109.up.railway.app/api/get-actor-data/?query=${actorName}`);
      const data = await response.json();

      if (!data || !data.filmography) {
        if (retries > 0) {
          console.warn(`Filmography missing for ${actorName}, retrying... (${retries} retries left)`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchActorData(actorName, retries - 1, delay);
        }
        else {
          throw new Error(`Failed to load filmography for ${actorName} after multiple attempts`);
        }
      }
      return data;
    }
    catch (error) {
      console.error("Error fetching actor data:", error);
      throw error;
    }
  };

  const validateActorSelection = async (actorName) => {
    if (actorName) {
      try {
        const data = await fetchActorData(actorName);
        if (!currentActor || !Array.isArray(currentActor.filmography) || currentActor.filmography.length === 0) {
          throw new Error(`Current actor's filmography is missing or invalid.`);
        }

        if(!actorPair.actor_2 || !Array.isArray(actorPair.actor_2.filmography) || actorPair.actor_2.filmography.length === 0) {
          throw new Error(`Target actor's filmography is missing or invalid.`);
        }
        const selectedActorFilmography = new Set(data.filmography.map(movie => movie.title));
        const currentActorFilmography = new Set(currentActor.filmography.map(movie => movie.title));
        const targetActorFilmography = new Set(actorPair.actor_2.filmography.map(movie => movie.title));

        const intersectionWithCurrent = new Set([...selectedActorFilmography].filter(movie => currentActorFilmography.has(movie)));
        const intersectionWithTarget = new Set([...selectedActorFilmography].filter(movie => targetActorFilmography.has(movie)));

        if (intersectionWithCurrent.size > 0) {
          setCurrentActor({ name: actorName, filmography: data.filmography });
          setConnectedMovies([...connectedMovies, intersectionWithCurrent]);
          setSelectedActor(actorName);
          setIncorrectSelection(false);
          setConnectedActors([...connectedActors, { name: actorName, filmography: data.filmography, image_url: data.image_url }]);
    
          if (intersectionWithTarget.size > 0) {
            setCompleteConnection(true);
            setConnectedMovies([...connectedMovies, intersectionWithCurrent, intersectionWithTarget]);
          }
        }
        else {
          setSelectedActor(actorName);
          setIncorrectSelection(true);
        }
      }
      catch (error) {
        setError(error.message);
        console.error(error);
      }
    }
  }

  const handleRemoveLastActor = () => {
    if (connectedActors.length > 0) {
      const updatedActors = [...connectedActors];
      updatedActors.pop();

      setConnectedActors(updatedActors);
      if (updatedActors.length > 0) {
        setCurrentActor(updatedActors[updatedActors.length - 1]);
      }
      else {
        setCurrentActor(actorPair.actor_1);
      }
      const updatedMovies = [...connectedMovies];
      updatedMovies.pop();
      setConnectedMovies(updatedMovies);
    }
  }

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Header date={date} setDate={setDate} />
        {error && <p>Error: {error}</p>}  {/* Show error message if there's an issue */}

        <CardSection actorPair = {actorPair} connectedMovies = {connectedMovies} connectedActors={connectedActors} handleRemoveLastActor={handleRemoveLastActor}
        completeConnection={completeConnection}/>
        <FeedbackSection selectedActor={selectedActor} incorrectSelection={incorrectSelection} completeConnection={completeConnection} connectedActors={connectedActors}/>
        <SearchSection completeConnection={completeConnection} setCompleteConnection={setCompleteConnection}
        searchQuery={searchQuery} setSearchQuery={setSearchQuery} handleSearchChange={handleSearchChange} searchResults={searchResults}
        setSearchResults={setSearchResults} handleSelectActor={handleSelectActor} connectedActors={connectedActors} actorPair={actorPair}/>
        <Footer/>
      </LocalizationProvider>
    </div>
  );
}

export default App;
